import type { HelpArticle } from "../../types";
import { W3_GEOMETRY } from "../../../calculations/calcPreviewFixtures";

const ft = (value: number): string => `${value} ft`;
const inches = (value: number): string => `${value.toFixed(3)} in`;
const lb = (value: number): string => `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} lb`;

export const memberGeometrySupportsArticle: HelpArticle = {
  id: "member-geometry-supports",
  title: "Geometry, supports & bearing",
  description: "Describe a wood member's span, support stations, bearing widths, overhangs, spacing, slope, and bracing without hiding the model the engine receives.",
  tags: ["wood", "span", "support", "bearing", "cantilever", "bracing", "geometry"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Card A defines the physical member the engine checks. The role decides which geometry controls appear; the values decide whether the sheet is a simple span or a general support layout. Firmark draws the same support stations and bearing widths alongside the inputs so the model can be checked before its demands are read.",
    },

    { kind: "heading", level: 2, text: "The role controls the geometry vocabulary", id: "role-fields" },
    { kind: "where", path: ["Calculations", "Maple St. Residence", W3_GEOMETRY.mark, "A. Key properties"] },
    {
      kind: "paragraph",
      text: "Bending roles expose clear span and base bearing. Spaced members also expose spacing; rafters add slope; built-up beams expose plies. Axial roles replace clear span with member height and, where the calculator uses it, effective-length factor. Controls that do not belong to the active role are absent rather than persisted as irrelevant zeroes.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "span_ft" },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "spacing_in" },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "bearing_length_in" },
    {
      kind: "shot",
      shotId: "wood-geometry-card",
      caption: "B-3's member identity, built-up section, span, bearing, bracing, and support summary remain in one card.",
    },
    {
      kind: "paragraph",
      text: "The section and the geometry are separate inputs. Selecting a different size or grade changes the section reference that is checked; it does not rewrite span, supports, loads, or conditions. Conversely, changing span or a support station keeps the chosen section seated and recomputes its result against the new model.",
    },

    { kind: "heading", level: 2, text: "Automatic simple supports", id: "automatic-supports" },
    {
      kind: "paragraph",
      text: "With no custom support rows, Firmark constructs the role's ordinary simple-span model from clear span and base bearing. The loading schematic shows a pin at the left and a roller at the right. In this state the support editor summarizes the automatic model instead of storing a redundant two-row table.",
    },
    {
      kind: "paragraph",
      text: "That compact state stays on the simple full-span calculation path while its loads also remain full-span uniform rows. Adding a support table, a cantilever, a point or partial load, a tapered row, or direct-shear deflection moves the same sheet onto the general beam contract. The route changes the analysis request; it does not replace the section, conditions, or named load rows already stored on the member.",
    },
    {
      kind: "steps",
      steps: [
        { title: "Enter clear span", body: "Use the clear distance between the bearing faces for the simple member. Leaving the field commits the value and recomputes the sheet." },
        { title: "Enter base bearing", body: "The base value supplies the bearing width at the simple end supports and seeds new custom support rows." },
        { title: "Read the diagram", body: "Confirm the support count, member length, and stated span before reading the output reactions or minimum-bearing result." },
      ],
    },

    { kind: "heading", level: 2, text: "Custom supports and overhangs", id: "custom-supports" },
    {
      kind: "paragraph",
      text: "Configure custom supports or cantilevers converts the simple model into explicit support rows. Each row carries a station measured in feet from the left end, a pin/roller/fixed type, and a bearing width in inches. Rows are sent to the engine in station order. The member-length field is the total distance from the left tip to the right tip; length beyond the last support is therefore a right overhang. A first support placed after station zero creates a left overhang.",
    },
    {
      kind: "paragraph",
      text: "Opening the editor seeds a pin at station zero and a roller at the current clear span, both using the base bearing. Add support places a new roller beyond the greatest entered station by the current span value; every station, type, and bearing remains editable. Reset to single span clears the explicit rows and member length, returning control to the clear-span model. A member length of zero likewise tells the general model to end at the last support.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "supports" },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "member_length_ft" },
    {
      kind: "shot",
      shotId: "wood-geometry-supports",
      caption: "B-3 declares three support stations and a 23 ft member, leaving 3 ft beyond the final support.",
    },
    {
      kind: "values",
      caption: `${W3_GEOMETRY.mark}: the exact support layout carried by the help fixture.`,
      rows: [
        { label: "Member", value: `${W3_GEOMETRY.section} · ${W3_GEOMETRY.species} ${W3_GEOMETRY.grade}` },
        { label: "Total member length", value: ft(W3_GEOMETRY.memberLengthFt) },
        { label: "Support 1", value: `${ft(W3_GEOMETRY.supports[0].pos)} · ${W3_GEOMETRY.supports[0].bearing} in bearing` },
        { label: "Support 2", value: `${ft(W3_GEOMETRY.supports[1].pos)} · ${W3_GEOMETRY.supports[1].bearing} in bearing` },
        { label: "Support 3", value: `${ft(W3_GEOMETRY.supports[2].pos)} · ${W3_GEOMETRY.supports[2].bearing} in bearing` },
        { label: "Right overhang", value: `${W3_GEOMETRY.memberLengthFt - W3_GEOMETRY.supports[2].pos} ft` },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Stations describe the analysis model",
      body: "Support type, station, bearing width, and total length are not drafting annotations. They enter the general beam model. Confirm that the entered idealization represents the intended restraint and load path; that engineering judgment is outside the UI control.",
    },

    { kind: "heading", level: 2, text: "Bearing input and bearing result are different", id: "bearing" },
    {
      kind: "paragraph",
      text: "Entered bearing is the available bearing width at a support. The results pane separately reports the engine's minimum required bearing length for each reaction. Keeping both values visible makes the comparison reviewable without turning the input into a derived value. Firmark does not enlarge the entered support when the minimum is greater; the sheet remains the record of what was declared.",
    },
    {
      kind: "paragraph",
      text: "In the automatic model, one base-bearing value serves both ends. Explicit rows carry their own bearing values into the general request, so a multi-bearing result can return a separate reaction and minimum for every station. Reordering positions in the editor does not define the engine order: the adapter sorts rows by station before compute, and the returned support results follow that physical sequence.",
    },
    {
      kind: "values",
      caption: `${W3_GEOMETRY.mark}: reactions and minimum bearing returned by the engine fixture.`,
      rows: W3_GEOMETRY.supports.map((support, index) => ({
        label: `Support ${index + 1} at ${support.pos} ft`,
        value: `${lb(support.reactionLb)} reaction · ${inches(support.minBearingIn)} minimum bearing`,
      })),
    },
    {
      kind: "shot",
      shotId: "wood-geometry-reactions",
      caption: "The output keeps all three support reactions and their individual minimum-bearing values attached to the computed geometry.",
    },

    { kind: "heading", level: 2, text: "Bracing belongs with the model", id: "bracing" },
    {
      kind: "paragraph",
      text: "Compression-edge bracing appears on bending roles because the choice is part of the stability input carried to the engine. The available labels describe the declared pattern; they do not verify that sheathing, blocking, connections, or construction details provide it. The engine's factor ledger and detailed checks report the stability consequence of the stored input after computation.",
    },
    { kind: "registry-field", calculatorId: "wood_beam_asd", fieldKey: "bracing_pattern" },
    {
      kind: "callout",
      variant: "note",
      title: "Engineering review boundary",
      body: "Firmark can show the entered restraint, reaction, minimum-bearing value, and engine provenance. Selecting an appropriate support idealization, bracing condition, or acceptable bearing detail requires project-specific engineering review.",
    },

    { kind: "related", topics: ["member-role-presets", "member-loads", "member-results", "linking-reactions"] },
  ],
};
